import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Ruler } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { useBodyMeasurements } from '../../hooks/useBodyMeasurements'
import { computeMeasurementDiffs } from '../../lib/phoenixUtils'
import { cn } from '../../lib/utils'
import { MeasurementForm } from './components/MeasurementForm'
import { MeasurementCard } from './components/MeasurementCard'
import { MeasurementChart } from './components/MeasurementChart'

const METRICS = ['neck', 'chest', 'belly', 'waist', 'arm', 'thigh', 'body_fat']

export function MeasurementsTab() {
  const { t } = useTranslation()
  const { measurements, loading, error, addMeasurement, deleteMeasurement, refetch } = useBodyMeasurements()
  const [selectedMetric, setSelectedMetric] = useState('waist')

  if (loading && measurements.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (error && measurements.length === 0) {
    return (
      <Card className="border-red-500/50">
        <CardContent className="text-center py-8">
          <p className="text-red-500 mb-4">{t('phoenixTracker.error')}</p>
          <Button onClick={refetch} variant="outline">{t('phoenixTracker.retry')}</Button>
        </CardContent>
      </Card>
    )
  }

  const measurementsWithDiffs = computeMeasurementDiffs(measurements)
  const showChart = measurements.length >= 2

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <MeasurementForm onSubmit={addMeasurement} loading={loading} />
        </CardContent>
      </Card>
      {error && (
        <p className="text-sm text-red-500 text-center">{error}</p>
      )}
      {showChart && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {METRICS.map((metric) => (
              <button
                key={metric}
                onClick={() => setSelectedMetric(metric)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                  selectedMetric === metric
                    ? 'bg-app-primary-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
              >
                {metric === 'body_fat' ? t('phoenixTracker.measurements.bodyFat') : t(`phoenixTracker.measurements.${metric}`)}
              </button>
            ))}
          </div>
          <MeasurementChart measurements={measurements} selectedMetric={selectedMetric} />
        </div>
      )}
      {measurementsWithDiffs.length === 0 ? (
        <EmptyState
          icon={Ruler}
          title={t('phoenixTracker.measurements.noEntries')}
        />
      ) : (
        <div className="space-y-3">
          {measurementsWithDiffs.map((m) => (
            <MeasurementCard
              key={m.id}
              measurement={m}
              onDelete={deleteMeasurement}
            />
          ))}
        </div>
      )}
    </div>
  )
}
